import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { Loader2 } from "lucide-react";
import { app } from "../firebase";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import ReferralSignups from "../components/ReferralSignups";
import ReferralDashboard from "../components/ReferralDashboard";
import { adminListPartners, adminFetchStats, adminApprovePartner, adminRemovePartner, adminSuspendPartner, adminFetchSignups, adminSetOutcome, adminAddPayment, adminRemovePayment, adminRemoveSignup, formatMoney, AdminPartner, ReferredAccount, ReferralStats } from "../utils/referral";

const suggestCode = (p: AdminPartner) =>
  (p.company || p.partnerName || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

const ReferralsAdmin: React.FC = () => {
  const auth = getAuth(app);
  const [user, loading] = useAuthState(auth);
  const [partners, setPartners] = useState<AdminPartner[] | null>(null);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<ReferredAccount[]>([]);

  const showAccounts = async (p: AdminPartner) => {
    if (!user) return;
    if (open === p.partnerUid) { setOpen(null); return; }
    try {
      setAccounts(await adminFetchSignups(user, p.partnerUid));
      setOpen(p.partnerUid);
    } catch {
      setMessage("Could not load that partner's accounts.");
    }
  };

  const removeAccount = async (p: AdminPartner, a: ReferredAccount) => {
    if (!user || !a.referredUid) return;
    try {
      await adminRemoveSignup(user, a.referredUid);
      setAccounts(await adminFetchSignups(user, p.partnerUid));
      setMessage(`Removed ${a.email ?? "the account"} from the program.`);
      load();
    } catch {
      setMessage("Could not remove that account. Please try again.");
    }
  };

  const refreshAccounts = async (p: AdminPartner) => {
    if (!user) return;
    setAccounts(await adminFetchSignups(user, p.partnerUid));
    load();
  };

  const addPayment = async (p: AdminPartner, a: ReferredAccount, amount: number, note: string) => {
    if (!user || !a.referredUid) return "Missing account id.";
    const problem = await adminAddPayment(user, a.referredUid, amount, note);
    if (!problem) await refreshAccounts(p);
    return problem;
  };

  const removePayment = async (p: AdminPartner, paymentId: number) => {
    if (!user) return;
    try {
      await adminRemovePayment(user, paymentId);
      await refreshAccounts(p);
    } catch {
      setMessage("Could not delete that payment. Please try again.");
    }
  };

  const resetAccount = async (p: AdminPartner, a: ReferredAccount) => {
    if (!user || !a.referredUid) return "Missing account id.";
    const problem = await adminSetOutcome(user, a.referredUid, "demo", 0);
    if (!problem) await refreshAccounts(p);
    return problem;
  };

  const load = () => {
    if (!user) return;
    adminListPartners(user)
      .then((list) => { setPartners(list); setError(null); })
      .catch((e: Error) => (/ 40[13]$/.test(e.message) ? setDenied(true) : setError("Could not load partners. Please try again in a moment.")));
    adminFetchStats(user).then(setStats).catch(() => setStats(null)); // the list is the page; the dashboard is a bonus
  };
  useEffect(load, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (p: AdminPartner) => {
    if (!user) return;
    const code = (codes[p.partnerUid] ?? p.code ?? suggestCode(p)).trim();
    const problem = await adminApprovePartner(user, p.partnerUid, code);
    setMessage(problem ?? `Approved ${p.partnerName} with code ${code}.`);
    if (!problem) load();
  };

  const suspend = async (p: AdminPartner) => {
    if (!user || !window.confirm(`Pause ${p.partnerName}? Their code stops recording new sign-ups. Their accounts and earnings are kept, and you can resume them.`)) return;
    try {
      await adminSuspendPartner(user, p.partnerUid);
      setMessage(`Paused ${p.partnerName}.`);
      load();
    } catch {
      setMessage("Could not pause that partner. Please try again.");
    }
  };

  const remove = async (p: AdminPartner) => {
    if (!user || !window.confirm(`Remove ${p.partnerName} (${p.company})? Accounts already attached to their code stay on record.`)) return;
    try {
      await adminRemovePartner(user, p.partnerUid);
      setMessage(`Removed ${p.partnerName}.`);
      load();
    } catch {
      setMessage("Could not remove that partner. Please try again.");
    }
  };

  return (
    <div className="flex flex-col h-screen bg-slate-50">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="referrals" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-5xl mx-auto space-y-6">
            <header>
              <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Referral partners</h1>
              <p className="text-slate-600 mt-1"><Link className="text-blue-600 underline" to="/referrals">Back to Referrals</Link></p>
            </header>
            {message && <p className="text-sm text-slate-700" role="status">{message}</p>}
            {stats && <ReferralDashboard stats={stats} />}
            {loading || (!partners && !denied && !error) ? (
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            ) : denied ? (
              <p className="text-red-700" role="alert">This page is for administrators.</p>
            ) : error ? (
              <p className="text-red-700" role="alert">{error}</p>
            ) : partners && partners.length === 0 ? (
              <p className="text-slate-600">No applications yet.</p>
            ) : (
              <div className="space-y-4">
                {partners!.map((p) => (
                  <section key={p.partnerUid} className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h2 className="text-lg font-bold text-slate-900">{p.partnerName} <span className="font-normal text-slate-600">({p.company})</span></h2>
                      <span className={`text-xs font-bold uppercase ${p.status === "approved" ? "text-emerald-700" : "text-amber-700"}`}>{p.status}</span>
                    </div>
                    <p className="text-sm text-slate-600">{p.partnerEmail} · {p.partnerType} · applied {new Date(p.createdAt).toLocaleDateString()}</p>
                    <p className="text-sm text-slate-800 mt-2 whitespace-pre-wrap">{p.details}</p>
                    {p.status !== "pending" && (
                      <p className="text-sm text-slate-700 mt-2">Code <strong>{p.code}</strong> · {p.visits} visits · {p.signups} accounts created · {formatMoney(p.earned ?? 0)} earned</p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                      <input
                        aria-label={`Code for ${p.partnerName}`}
                        className="bg-slate-50 border border-slate-300 rounded-xl py-1.5 px-3 font-mono text-sm"
                        value={codes[p.partnerUid] ?? p.code ?? suggestCode(p)}
                        onChange={(e) => setCodes({ ...codes, [p.partnerUid]: e.target.value })}
                      />
                      <button className="btn-primary" onClick={() => approve(p)}>{p.status === "pending" ? "Approve" : p.status === "suspended" ? "Resume" : "Change code"}</button>
                      {p.status === "approved" && (
                        <button className="text-amber-700 underline text-sm" onClick={() => suspend(p)}>Pause</button>
                      )}
                      {p.status !== "pending" && (
                        <button className="text-blue-600 underline text-sm" onClick={() => showAccounts(p)}>{open === p.partnerUid ? "Hide accounts" : "Show accounts"}</button>
                      )}
                      <button className="text-red-700 underline text-sm" onClick={() => remove(p)}>Remove</button>
                    </div>
                    {open === p.partnerUid && (
                      <div className="mt-4">
                        <ReferralSignups accounts={accounts} onAddPayment={(a, amount, note) => addPayment(p, a, amount, note)} onRemovePayment={(_, id) => removePayment(p, id)} onReset={(a) => resetAccount(p, a)} onRemove={(a) => removeAccount(p, a)} />
                      </div>
                    )}
                  </section>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ReferralsAdmin;
